import { ColumnHeaderModel, ColumnSource, GridSource, OptionSource, RowSource } from "@omegagrid/grid";

/**
 * Demo data of the table style editor: a few small reports typical for
 * finance the theme is previewed on. The values are generated
 * deterministically, so the same numbers show up on every open. Row and
 * column names follow the analytics grid conventions (R_<row id>,
 * C_<column>_<indicator>), so the name matchers authored here read the same
 * way as on the real reports.
 */

export type DemoDataset = {
	id: string;
	title: string;
	create: () => GridSource;
}

type RowSpec = {
	id: string;
	label: string;
	/** starting amount of a leaf row, the columns vary it */
	base?: number;
	/** named amounts of a leaf row, picked by the column key */
	data?: Record<string, number>;
	/** sign the row contributes to its parent with, e.g. -1 for the costs of a result */
	coef?: number;
	/** the amount does not vary between the columns (share capital, depreciation) */
	flat?: boolean;
	children?: RowSpec[];
	/** computed row: sum of [row id, coefficient] pairs */
	calc?: [string, number][];
}

type FlatRow = RowSpec & {
	level: number;
	parent: FlatRow|null;
	values: Record<string, number>;
}

/** value of another row (by id) in a column (by name) */
type ValueOf = (id: string, column: string) => number;

type ColumnSpec = {
	name: string;
	/** two header rows: group and label; adjacent columns of one group share a spanned header cell */
	header: [string, string];
	/** key into the row data / generator, free form */
	key?: string;
	/** number format, whole amounts by default */
	z?: string;
	w?: number;
	/** derived column computed per row from the other columns of the same row */
	derive?: (value: (column: string) => number) => number;
}

type SourceSpec = {
	/** header rows of the row label column */
	textHeader: [string, string];
	textWidth?: number;
	columns: ColumnSpec[];
	rows: RowSpec[];
	/** value of a leaf row in a base (non-derived) column */
	leaf: (row: FlatRow, column: ColumnSpec, value: ValueOf) => number;
}

const AMOUNT = '#,##0';
const PERCENT = '0.0%';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** seasonality of the monthly amounts */
const SEASON = [0.92, 0.95, 1.02, 1.0, 1.04, 1.06, 0.9, 0.85, 1.05, 1.1, 1.08, 1.15];
const QUARTERS = [0.96, 1.02, 0.93, 1.09];

const GRID_OPTIONS: OptionSource = {
	rowNumbers: true,
	colLetters: false,
	rowTree: true,
	columnTree: false,
	editable: true,
	autofill: true,
	undoRedo: true,
	commandWindow: false,
	freezeLeft: 1,
	fontSize: 12,
	scrolling: {scrollbarVisibilityTreshold: 0},
};

/** deterministic generator (mulberry32) - the demo shows the same numbers on every open */
function createRandom(seed: number) {
	let state = seed >>> 0;
	return () => {
		state = (state + 0x6D2B79F5) >>> 0;
		let t = state;
		t = Math.imul(t ^ (t >>> 15), t | 1);
		t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}

/** multiplier within 1 ± spread */
const vary = (random: () => number, spread: number) => 1 + (random() * 2 - 1) * spread;

const ratio = (part: number, whole: number) => whole ? part / Math.abs(whole) : 0;

const pad2 = (n: number) => n.toString().padStart(2, '0');

function flattenRows(specs: RowSpec[], level = 0, parent: FlatRow = null, out: FlatRow[] = []): FlatRow[] {
	for (const spec of specs) {
		const row: FlatRow = {...spec, level, parent, values: {}};
		out.push(row);
		if (spec.children) flattenRows(spec.children, level + 1, row, out);
	}
	return out;
}

/** adjacent columns with the same group label share one spanned header cell, like the analytics column axis */
function mergeHeaderGroups(columns: ColumnSource[]) {
	let current: ColumnHeaderModel;
	for (const column of columns) {
		const header = column.header as ColumnHeaderModel[];
		if (current && header[0].label && header[0].label === current.label) {
			current.cs++;
			header[0] = {};
		} else {
			current = header[0];
			current.cs = 1;
		}
	}
}

function createSource(spec: SourceSpec): GridSource {
	const rows = flattenRows(spec.rows);
	const byId = new Map(rows.map(row => [row.id, row]));
	const columns = new Map(spec.columns.map(column => [column.name, column]));

	// derived columns are computed per row from the row's own values (the
	// percentage of a parent is the percentage of its totals, not the sum of
	// the children's percentages), parents sum their children, calc rows sum
	// the referenced rows and leaves get generated values
	const value: ValueOf = (id, name) => {
		const row = byId.get(id);
		const column = columns.get(name);
		if (!row || !column) return 0;
		if (row.values[name] != null) return row.values[name];

		let result: number;
		if (column.derive) {
			result = column.derive(other => value(id, other));
		} else if (row.calc) {
			result = row.calc.reduce((sum, [ref, coef]) => sum + coef * value(ref, name), 0);
		} else if (row.children) {
			result = rows.filter(r => r.parent === row).reduce((sum, child) => sum + (child.coef ?? 1) * value(child.id, name), 0);
		} else {
			result = spec.leaf(row, column, value);
		}

		row.values[name] = result;
		return result;
	};

	const textColumn: ColumnSource = {
		t: 't',
		w: spec.textWidth ?? 280,
		data: 0,
		name: 'C_DIM_1',
		levelIndent: true,
		levelButtons: true,
		sorting: true,
		header: [{label: spec.textHeader[0]}, {label: spec.textHeader[1]}],
	};

	const valueColumns: ColumnSource[] = spec.columns.map((column, i) => ({
		t: 'n',
		w: column.w ?? 110,
		z: column.z ?? AMOUNT,
		data: i + 1,
		name: column.name,
		sorting: true,
		header: [{label: column.header[0]}, {label: column.header[1]}],
	}));
	mergeHeaderGroups(valueColumns);

	const round = (v: number, column: ColumnSpec) => column.z == PERCENT ? Math.round(v * 10000) / 10000 : Math.round(v);

	const rowSources: RowSource[] = rows.map(row => ({
		name: `R_${row.id}`,
		l: row.level,
		data: [row.label, ...spec.columns.map(column => round(value(row.id, column.name), column))],
	}));

	return {
		columns: [textColumn, ...valueColumns],
		rows: rowSources,
		options: {...GRID_OPTIONS},
	};
}

/** profit and loss statement by month, statement rows with subtotals and results */
const createProfitAndLoss = (): GridSource => {
	const random = createRandom(2025);
	const months: ColumnSpec[] = MONTHS.map((month, i) => ({
		name: `C_2025${pad2(i + 1)}_AMOUNT_1`,
		header: [`${month} 2025`, 'Actual'],
		key: String(i),
	}));

	return createSource({
		textHeader: ['', 'Statement row'],
		columns: [...months, {
			name: 'C_2025_AMOUNT_1',
			header: ['Total 2025', 'Actual'],
			derive: value => months.reduce((sum, month) => sum + value(month.name), 0),
		}],
		rows: [
			{id: '10', label: 'Total revenue', children: [
				{id: '11', label: 'Revenue from products and services', base: 4200000},
				{id: '12', label: 'Revenue from goods sold', base: 1350000},
				{id: '13', label: 'Other operating income', base: 180000},
			]},
			{id: '20', label: 'Total costs', children: [
				{id: '21', label: 'Materials and services', base: 2650000},
				{id: '22', label: 'Personnel costs', base: 1480000},
				{id: '23', label: 'Depreciation', base: 310000, flat: true},
				{id: '24', label: 'Other operating expenses', base: 240000},
			]},
			{id: '30', label: 'Operating result', calc: [['10', 1], ['20', -1]]},
			{id: '40', label: 'Financial result', children: [
				{id: '41', label: 'Financial income', base: 25000},
				{id: '42', label: 'Financial expenses', base: 95000, coef: -1},
			]},
			{id: '50', label: 'Profit before tax', calc: [['30', 1], ['40', 1]]},
			{id: '60', label: 'Income tax', calc: [['50', 0.21]]},
			{id: '70', label: 'Profit after tax', calc: [['50', 1], ['60', -1]]},
		],
		leaf: (row, column) => row.flat
			? row.base
			: Math.round(row.base * SEASON[Number(column.key)] * vary(random, 0.06)),
	});
}

/** balance sheet at three year ends with the year-over-year change */
const createBalanceSheet = (): GridSource => {
	const random = createRandom(2024);
	const years = [2023, 2024, 2025];
	const growth = new Map<string, number>();
	const rateOf = (id: string) => {
		if (!growth.has(id)) growth.set(id, -0.04 + random() * 0.16);
		return growth.get(id);
	};

	const yearColumns: ColumnSpec[] = years.map((year, i) => ({
		name: `C_${year}_AMOUNT_1`,
		header: [`31 Dec ${year}`, 'Net'],
		key: String(i),
	}));
	const [, previous, current] = yearColumns;

	return createSource({
		textHeader: ['', 'Balance sheet item'],
		columns: [...yearColumns, {
			name: 'C_VAR_A_1',
			header: ['Change 2025/2024', 'Amount'],
			derive: value => value(current.name) - value(previous.name),
		}, {
			name: 'C_VAR_P_1',
			header: ['Change 2025/2024', '%'],
			z: PERCENT,
			derive: value => ratio(value(current.name) - value(previous.name), value(previous.name)),
		}],
		rows: [
			{id: 'A', label: 'TOTAL ASSETS', children: [
				{id: 'A_I', label: 'Fixed assets', children: [
					{id: 'A_I_1', label: 'Intangible assets', base: 1850000},
					{id: 'A_I_2', label: 'Tangible assets', base: 24600000},
					{id: 'A_I_3', label: 'Financial investments', base: 3200000},
				]},
				{id: 'A_II', label: 'Current assets', children: [
					{id: 'A_II_1', label: 'Inventories', base: 6900000},
					{id: 'A_II_2', label: 'Receivables', base: 9750000},
					{id: 'A_II_3', label: 'Cash and cash equivalents', base: 4300000},
				]},
				{id: 'A_III', label: 'Prepaid expenses', base: 420000},
			]},
			{id: 'P', label: 'TOTAL EQUITY AND LIABILITIES', children: [
				{id: 'P_I', label: 'Equity', children: [
					{id: 'P_I_1', label: 'Share capital', base: 10000000, flat: true},
					{id: 'P_I_2', label: 'Reserve funds', base: 2000000, flat: true},
					{id: 'P_I_3', label: 'Retained earnings', base: 14200000},
					{id: 'P_I_4', label: 'Profit for the period', base: 5100000},
				]},
				{id: 'P_II', label: 'Liabilities', children: [
					{id: 'P_II_1', label: 'Provisions', base: 900000},
					{id: 'P_II_2', label: 'Long-term liabilities', base: 8500000},
					{id: 'P_II_3', label: 'Short-term liabilities'},
				]},
				{id: 'P_III', label: 'Accrued expenses', base: 520000},
			]},
		],
		leaf: (row, column, value) => {
			if (row.id == 'P_II_3') {
				// short-term payables balance the sheet: assets less all the other liabilities
				return value('A', column.name) - ['P_I', 'P_II_1', 'P_II_2', 'P_III'].reduce((sum, id) => sum + value(id, column.name), 0);
			}
			if (row.flat) return row.base;
			// the middle year is the base, the neighbours grow/shrink by the row's rate
			return Math.round(row.base * Math.pow(1 + rateOf(row.id), Number(column.key) - 1) * vary(random, 0.03));
		},
	});
}

/** costs of the cost centres by quarter: actual, plan and the variances */
const createBudgetVsActual = (): GridSource => {
	const random = createRandom(2031);
	const columns: ColumnSpec[] = [];

	for (let q = 0; q < 4; q++) {
		const group = `Q${q + 1} 2025`;
		const actual = `C_2025Q${q + 1}_AMOUNT_1`;
		const plan = `C_2025Q${q + 1}_PLAN_1`;
		columns.push({
			name: actual,
			header: [group, 'Actual'],
			key: `actual:${q}`,
		}, {
			name: plan,
			header: [group, 'Budget'],
			key: `plan:${q}`,
		}, {
			name: `C_2025Q${q + 1}_VAR_A_1`,
			header: [group, 'Variance'],
			derive: value => value(actual) - value(plan),
		}, {
			name: `C_2025Q${q + 1}_VAR_P_1`,
			header: [group, 'Variance %'],
			z: PERCENT,
			derive: value => ratio(value(actual) - value(plan), value(plan)),
		});
	}

	return createSource({
		textHeader: ['', 'Cost centre'],
		textWidth: 240,
		columns,
		rows: [
			{id: '100', label: '100 Administration', children: [
				{id: '110', label: '110 Management', base: 620000},
				{id: '120', label: '120 Finance and controlling', base: 410000},
				{id: '130', label: '130 IT', base: 530000},
			]},
			{id: '200', label: '200 Production', children: [
				{id: '210', label: '210 Production – hall A', base: 2900000},
				{id: '220', label: '220 Production – hall B', base: 2450000},
				{id: '230', label: '230 Maintenance', base: 380000},
			]},
			{id: '300', label: '300 Sales', children: [
				{id: '310', label: '310 Domestic sales', base: 760000},
				{id: '320', label: '320 Export', base: 540000},
				{id: '330', label: '330 Marketing', base: 290000},
			]},
			{id: 'TOTAL', label: 'Total', calc: [['100', 1], ['200', 1], ['300', 1]]},
		],
		leaf: (row, column) => {
			const [kind, quarter] = column.key.split(':');
			const planned = Math.round(row.base * 3 * QUARTERS[Number(quarter)]);
			return kind == 'plan' ? planned : Math.round(planned * vary(random, 0.12));
		},
	});
}

/** revenue, costs and margin of the projects grouped by customer (items model) */
const createProjectMargins = (): GridSource => createSource({
	textHeader: ['', 'Customer / project'],
	textWidth: 300,
	columns: [
		{name: 'C_REVENUE_1', header: ['Revenue', 'Amount'], key: 'revenue'},
		{name: 'C_COSTS_1', header: ['Costs', 'Amount'], key: 'costs'},
		{name: 'C_MARGIN_1', header: ['Margin', 'Amount'], derive: value => value('C_REVENUE_1') - value('C_COSTS_1')},
		{name: 'C_MARGIN_P_1', header: ['Margin', '%'], z: PERCENT, derive: value => ratio(value('C_REVENUE_1') - value('C_COSTS_1'), value('C_REVENUE_1'))},
		{name: 'C_HOURS_1', header: ['Hours worked', 'h'], key: 'hours', w: 100},
	],
	rows: [
		{id: 'ALPHA', label: 'Alpha Construction Ltd.', children: [
			{id: 'P-2025-014', label: 'P-2025-014 Production hall refurbishment', data: {revenue: 8450000, costs: 6900000, hours: 4120}},
			{id: 'P-2025-021', label: 'P-2025-021 Warehouse extension', data: {revenue: 5120000, costs: 4380000, hours: 2650}},
			{id: 'P-2025-033', label: 'P-2025-033 Service and maintenance', data: {revenue: 1240000, costs: 890000, hours: 780}},
		]},
		{id: 'BETA', label: 'Beta Logistics plc', children: [
			{id: 'P-2025-008', label: 'P-2025-008 Conveyor line', data: {revenue: 12300000, costs: 10950000, hours: 6480}},
			{id: 'P-2025-027', label: 'P-2025-027 Control system upgrade', data: {revenue: 3870000, costs: 3990000, hours: 2210}},
		]},
		{id: 'GAMMA', label: 'Gamma Energy Ltd.', children: [
			{id: 'P-2025-011', label: 'P-2025-011 22 kV substation', data: {revenue: 9650000, costs: 7720000, hours: 5030}},
			{id: 'P-2025-019', label: 'P-2025-019 500 kWp solar plant', data: {revenue: 6780000, costs: 5610000, hours: 3140}},
			{id: 'P-2025-030', label: 'P-2025-030 Cable routes', data: {revenue: 2150000, costs: 2080000, hours: 1390}},
		]},
		{id: 'TOTAL', label: 'Total', calc: [['ALPHA', 1], ['BETA', 1], ['GAMMA', 1]]},
	],
	leaf: (row, column) => row.data?.[column.key] ?? 0,
});

/** receivables by due-date buckets, customers grouped by sales representative (aging model) */
const createReceivablesAging = (): GridSource => {
	const buckets = ['C_NOT_DUE_1', 'C_DUE_1_30_1', 'C_DUE_31_60_1', 'C_DUE_61_90_1', 'C_DUE_90_1'];
	return createSource({
		textHeader: ['', 'Sales rep / customer'],
		textWidth: 240,
		columns: [
			{name: 'C_TOTAL_1', header: ['Receivables', 'Total'], derive: value => buckets.reduce((sum, bucket) => sum + value(bucket), 0)},
			{name: 'C_NOT_DUE_1', header: ['Receivables', 'Not yet due'], key: 'notDue'},
			{name: 'C_DUE_1_30_1', header: ['Overdue', '1–30 days'], key: 'd30'},
			{name: 'C_DUE_31_60_1', header: ['Overdue', '31–60 days'], key: 'd60'},
			{name: 'C_DUE_61_90_1', header: ['Overdue', '61–90 days'], key: 'd90'},
			{name: 'C_DUE_90_1', header: ['Overdue', 'over 90 days'], key: 'd90plus'},
			{name: 'C_OVERDUE_P_1', header: ['Overdue', '% of total'], z: PERCENT, derive: value => ratio(value('C_TOTAL_1') - value('C_NOT_DUE_1'), value('C_TOTAL_1'))},
		],
		rows: [
			{id: 'REP1', label: 'John Miller', children: [
				{id: '1001', label: 'Alpha Construction Ltd.', data: {notDue: 1845000, d30: 312000, d60: 0, d90: 0, d90plus: 0}},
				{id: '1002', label: 'Delta Retail Ltd.', data: {notDue: 620000, d30: 148000, d60: 96000, d90: 41000, d90plus: 0}},
				{id: '1003', label: 'Epsilon Pharma plc', data: {notDue: 2410000, d30: 0, d60: 0, d90: 0, d90plus: 0}},
				{id: '1004', label: 'Zeta Agro Co-op', data: {notDue: 385000, d30: 92000, d60: 118000, d90: 74000, d90plus: 265000}},
			]},
			{id: 'REP2', label: 'Jane Cooper', children: [
				{id: '2001', label: 'Beta Logistics plc', data: {notDue: 3120000, d30: 455000, d60: 0, d90: 0, d90plus: 0}},
				{id: '2002', label: 'Gamma Energy Ltd.', data: {notDue: 1980000, d30: 0, d60: 0, d90: 0, d90plus: 0}},
				{id: '2003', label: 'Eta Tech Ltd.', data: {notDue: 240000, d30: 88000, d60: 61000, d90: 0, d90plus: 132000}},
				{id: '2004', label: 'Theta Media Ltd.', data: {notDue: 0, d30: 0, d60: 54000, d90: 39000, d90plus: 418000}},
			]},
			{id: 'TOTAL', label: 'Total', calc: [['REP1', 1], ['REP2', 1]]},
		],
		leaf: (row, column) => row.data?.[column.key] ?? 0,
	});
}

export const DEMO_DATASETS: DemoDataset[] = [
	{id: 'profit-loss', title: 'Profit and loss by month', create: createProfitAndLoss},
	{id: 'balance-sheet', title: 'Balance sheet', create: createBalanceSheet},
	{id: 'budget-vs-actual', title: 'Budget vs. actual by cost centre', create: createBudgetVsActual},
	{id: 'project-margins', title: 'Project margins', create: createProjectMargins},
	{id: 'receivables-aging', title: 'Receivables aging', create: createReceivablesAging},
];

/** a fresh source of the dataset (the first one for an unknown id) */
export function createDemoSource(id: string): GridSource {
	return (DEMO_DATASETS.find(dataset => dataset.id == id) ?? DEMO_DATASETS[0]).create();
}
